import React from 'react';
import { MessageSquare, CalendarCheck, CheckCircle2, Trophy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { usd } from './HeadlineStrip';

export function OutcomeFunnel({ report }) {
  const f = report.funnel || {};
  const stages = [
    { icon: MessageSquare, label: 'Positive replies & interest', value: f.positive_replies ?? 0, testId: 'funnel-replies', color: '#0ea5e9' },
    { icon: CalendarCheck, label: 'Meetings booked', value: f.meetings_booked ?? 0, testId: 'funnel-booked', color: '#6366f1' },
    { icon: CheckCircle2, label: 'Meetings held', value: f.meetings_held ?? 0, testId: 'funnel-held', color: '#059669' },
    { icon: Trophy, label: 'Deals won', value: f.deals_won ?? 0, testId: 'funnel-won', color: '#d97706' },
  ];
  const top = Math.max(...stages.map(s => s.value), 1);
  const rate = (a, b) => (b > 0 ? Math.round((a / b) * 100) : null);

  return (
    <Card data-testid="leo-impact-funnel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base md:text-lg">Outcome funnel — from interest to revenue</CardTitle>
        <p className="text-xs text-muted-foreground">
          How LEO's activity converts down the pipeline. Meetings only count as "held" once the outcome is recorded, so this is a true ROI view — not an activity vanity count.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {stages.map((s, i) => {
            const prev = i > 0 ? stages[i - 1].value : null;
            const conv = i > 0 ? rate(s.value, prev) : null;
            const width = Math.max((s.value / top) * 100, s.value > 0 ? 8 : 3);
            return (
              <div key={s.label} className="flex items-center gap-3" data-testid={s.testId}>
                <div className="flex items-center gap-2 w-48 shrink-0">
                  <s.icon className="h-4 w-4" style={{ color: s.color }} />
                  <span className="text-sm text-muted-foreground">{s.label}</span>
                </div>
                <div className="flex-1 flex items-center gap-3">
                  <div className="flex-1 h-8 rounded-lg bg-muted/40 overflow-hidden">
                    <div
                      className="h-full rounded-lg flex items-center px-3 transition-all"
                      style={{ width: `${width}%`, backgroundColor: `${s.color}22`, borderRight: `3px solid ${s.color}` }}
                    >
                      <span className="text-sm font-semibold tabular-nums" data-testid={`${s.testId}-value`}>{s.value}</span>
                    </div>
                  </div>
                  {conv != null && (
                    <span className="text-xs text-muted-foreground w-24 text-right tabular-nums" data-testid={`${s.testId}-rate`}>
                      {conv}% of prev
                    </span>
                  )}
                  {conv == null && <span className="w-24" />}
                </div>
              </div>
            );
          })}
        </div>

        {(f.meetings_awaiting_outcome ?? 0) > 0 && (
          <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-3" data-testid="funnel-awaiting-note">
            {f.meetings_awaiting_outcome} past meeting{f.meetings_awaiting_outcome === 1 ? '' : 's'} still awaiting an outcome — record them in the Meeting Scheduler so "held" stays accurate.
          </p>
        )}

        <div className="grid grid-cols-2 gap-3 mt-5">
          <div className="rounded-xl border border-border p-4" data-testid="funnel-pipeline-value">
            <p className="text-xs text-muted-foreground">Open pipeline value</p>
            <p className="text-2xl font-bold tabular-nums mt-1">{usd(f.pipeline_value_usd)}</p>
          </div>
          <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4" data-testid="funnel-revenue-value">
            <p className="text-xs text-muted-foreground">Revenue won this period</p>
            <p className="text-2xl font-bold tabular-nums mt-1 text-emerald-600 dark:text-emerald-400">{usd(f.revenue_won_usd)}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
